import React, { useCallback } from 'react'
import AsyncSearchSelect, { SearchOption } from '@/components/common/AsyncSearchSelectResponsive'
import { apiClient } from '@/services/api'
import { getCollectionFromDeptId } from '@/utils/collection'
import type { EmployeeDTO, DepartmentDTO } from '@/types'

/**
 * 基础信息数据接口
 */
export interface BasicInfo {
  applicantId: string
  applicantName: string
  applicantDisplayNumber: string
  applicantDeptId: string
  applicantDepartment: string
  /** 申请人所属主体 ID，如 "GSX01"，用于部门搜索过滤 */
  applicantCollection?: string
}

interface BasicInfoSectionProps {
  /**
   * 当前基础信息
   */
  basicInfo: BasicInfo

  /**
   * 信息变更回调
   */
  onChange: (basicInfo: BasicInfo) => void

  /**
   * 申请人字段是否只读（新建模式下自动填入当前登录人后置灰）
   */
  isApplicantReadonly?: boolean
  /**
   * 整体只读（查看模式）
   */
  disabled?: boolean
}

/**
 * 基础信息部分组件
 * 包含申请人和部门的异步搜索下拉
 * 响应式设计：移动端单列，PC 端双列
 */
const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  basicInfo,
  onChange,
  isApplicantReadonly = false,
  disabled = false,
}) => {
  /** 格式化员工展示文本：姓名-工号-域账号，缺失字段自动跳过 */
  const formatEmployeeDisplay = (name: string, displayNumber: string, domain: string): string =>
    [name, displayNumber, domain].filter(Boolean).join('-')
  /**
   * 搜索员工 - 直接复用 API 服务
   */
  const handleSearchEmployees = useCallback(
    async (keyword: string) => {
      try {
        const results = await apiClient.searchEmployees(keyword)
        return results
      } catch (error) {
        console.error('搜索员工失败:', error)
        return []
      }
    },
    []
  )

  /**
   * 搜索部门 - 仅同主体部门（与申请人主体一致）
   */
  const handleSearchDepartments = useCallback(
    async (keyword: string, collectionOverride?: string) => {
      try {
        // 优先使用传入的 override（申请人刚选中时 state 尚未更新），其次读 state，最后从部门路径推导
        const collection = collectionOverride || basicInfo.applicantCollection || getCollectionFromDeptId(basicInfo.applicantDeptId)
        const results = await apiClient.searchDepartments(keyword, collection)
        return results
      } catch (error) {
        console.error('搜索部门失败:', error)
        return []
      }
    },
    [basicInfo.applicantCollection, basicInfo.applicantDeptId]
  )

  /**
   * 申请人选中时的回调
   * 自动填充部门信息（联动逻辑）
   * 如果员工数据中没有部门名称，则根据部门编号查询部门信息
   */
  const handleApplicantChange = async (
    value: string,
    label: string,
    option?: SearchOption
  ) => {
    // 处理清空情况
    if (!value || value.trim() === '') {
      onChange({
        ...basicInfo,
        applicantId: '',
        applicantName: '',
        applicantDisplayNumber: '',
        applicantDeptId: '',
        applicantDepartment: '',
      })
      return
    }

    const employee = option as EmployeeDTO | undefined
    const empName = employee?.commonName || label
    const empId = employee?.domain || value
    const empDisplayNumber = employee?.displayNumber || ''
    const deptId = employee?.department || ''
    let deptName = employee?.departmentName || ''

    // 如果有部门ID但没有部门名称，则查询部门信息
    if (deptId && !deptName) {
      try {
        const departments = await handleSearchDepartments(deptId, employee?.collection)
        if (departments && departments.length > 0) {
          // 优先精确匹配 number，避免树形展开后取到错误的父/子部门
          const dept = departments.find(d => d.number === deptId) || departments[0]
          deptName = dept.name || dept.shortName || dept.number || ''
        }
      } catch (error) {
        console.error('查询部门信息失败:', error)
      }
    }

    const newBasicInfo = {
      ...basicInfo,
      applicantId: empId,
      applicantName: empName,
      applicantDisplayNumber: empDisplayNumber,
      applicantDeptId: deptId,
      applicantDepartment: deptName,
      applicantCollection: employee?.collection || undefined,
    }

    onChange(newBasicInfo)
  }

  /**
   * 部门选中时的回调
   */
  const handleDepartmentChange = (
    value: string,
    label: string,
    option?: SearchOption
  ) => {
    // 处理清空情况
    if (!value || value.trim() === '') {
      onChange({
        ...basicInfo,
        applicantDeptId: '',
        applicantDepartment: '',
      })
      return
    }

    const department = option as DepartmentDTO | undefined
    // 使用 number 作为部门ID（因为 DepartmentDTO 没有 id 字段）
    const deptId = department?.number || value

    onChange({
      ...basicInfo,
      applicantDeptId: deptId,                  // 使用 number 作为部门 ID（DepartmentDTO 无独立 id 字段）
      applicantDepartment: label,
    })
  }

  /**
   * 格式化员工选项，显示头像和双行布局
   * 头像：员工头像或首字母占位符
   * 第一行：员工名称
   * 第二行：域账户-员工编号
   */
  const formatEmployeeOption = (option: SearchOption) => {
    const employee = option as EmployeeDTO
    return (
    <div className="flex items-center gap-3">
      {/* 头像部分 */}
      <div className="flex-shrink-0">
        {employee.avatarUrl ? (
          <img
            src={employee.avatarUrl}
            alt={employee.commonName}
            className="w-8 h-8 rounded-full object-cover bg-gray-200"
            onError={(e) => {
              // 图片加载失败时，隐藏 img 元素，显示占位符
              e.currentTarget.style.display = 'none'
              e.currentTarget.nextElementSibling?.classList.remove('hidden')
            }}
          />
        ) : null}
        {/* 默认头像占位符（图片加载失败或无头像时显示） */}
        <div
          className={`w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-semibold text-blue-600 ${
            employee.avatarUrl ? 'hidden' : ''
          }`}
        >
          {employee.commonName?.[0] || '?'}
        </div>
      </div>

      {/* 信息部分 */}
      <div className="flex-1 min-w-0">
        <p className="text-clay-sm font-medium text-gray-900 truncate">
          {employee.commonName}
        </p>
        <p className="text-clay-xs text-gray-500 truncate">
            {employee.displayNumber || ''}
            {employee.displayNumber && employee.domain ? '-' : ''}
            {employee.domain || ''}
          </p>
      </div>
    </div>
    )
  }

  /**
   * 格式化部门选项，显示双行布局
   * 第一行：部门名称（优先显示 name，否则显示 shortName）
   * 第二行：部门路径名称（如："途途课堂-TT业务线-途途测试3"）
   */
  const formatDepartmentOption = (option: SearchOption) => {
    const department = option as DepartmentDTO
    const deptName = department.name || department.shortName || ''
    const deptPath = department.departmentPathName || ''

    return (
      <div className="flex flex-col">
        <p className="text-clay-sm font-medium text-gray-900 truncate">
          {deptName}
        </p>
        {deptPath && (
          <p className="text-clay-xs text-gray-400 truncate">
            {deptPath}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-clay-md">
      {/* a. 申请人、费用承担部门各占一行 */}

      {/* 申请人搜索 - 单独一行 */}
      <div>
        <AsyncSearchSelect
          label="申请人"
          value={basicInfo.applicantId}
          displayText={formatEmployeeDisplay(basicInfo.applicantName, basicInfo.applicantDisplayNumber, basicInfo.applicantId)}
          onSearch={handleSearchEmployees}
          onChange={handleApplicantChange}
          placeholder="输入申请人姓名搜索..."
          required={true}
          formatOption={formatEmployeeOption}
          disabled={disabled || isApplicantReadonly}
        />
        {isApplicantReadonly && (
          <p className="hint-text mt-1">申请人默认为当前登录人，不可修改</p>
        )}
        {!isApplicantReadonly && disabled && (
          <p className="hint-text mt-1">查看模式下基础信息不可修改</p>
        )}
      </div>

      {/* b. 费用承担部门 - 单独一行 */}
      <div>
        <AsyncSearchSelect
          label="费用承担部门"
          value={basicInfo.applicantDeptId}
          displayText={basicInfo.applicantDepartment}
          onSearch={handleSearchDepartments}
          onChange={handleDepartmentChange}
          placeholder="输入部门名称搜索..."
          required={true}
          formatOption={formatDepartmentOption}
          disabled={disabled}
        />

        {/* c. 费用承担部门下方的提示文案 */}
        <div className="info-box mt-2">
          <p className="hint-text leading-relaxed text-gray-700">
            <span className="font-semibold text-gray-900">"费用承担部门"</span>字段默认为申请人所在部门，可根据实际情况选择：
          </p>
          <ul className="mt-2 space-y-1 hint-text text-gray-700 ml-4 list-decimal">
            <li>如费用由申请人所在部门承担，无需修改；</li>
            <li>如费用由其他指定部门承担，请填写实际承担费用的部门名称；</li>
            <li>该信息用于记录出差人本次差旅的费用归属部门，将作为后续报销、成本核算的依据，请务必准确填写。</li>
          </ul>
        </div>
      </div>

      {/* 显示当前选中的信息 */}
      {basicInfo.applicantId && (
        <div className="info-box">
          {/* PC端：一行显示；移动端：两行显示 */}
          <p className="body-text text-blue-800 hidden md:block">
            <span className="font-medium">当前申请人:</span> {formatEmployeeDisplay(basicInfo.applicantName, basicInfo.applicantDisplayNumber, basicInfo.applicantId)}
            {' · '}
            <span className="font-medium">费用承担部门:</span> {basicInfo.applicantDepartment}
          </p>
          <div className="body-text text-blue-800 md:hidden space-y-1">
            <p>
              <span className="font-medium">当前申请人:</span> {formatEmployeeDisplay(basicInfo.applicantName, basicInfo.applicantDisplayNumber, basicInfo.applicantId)}
            </p>
            <p>
              <span className="font-medium">费用承担部门:</span> {basicInfo.applicantDepartment}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default BasicInfoSection
